import React from "react";
import { Check, Clock, FileText, Zap, Pencil, Ban, ChevronDown, ChevronUp, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Process, ModalityStage } from "@/types/process";
import { Document } from "@/types/document";
import GeneratedDocumentLink from "@/components/chat/GeneratedDocumentLink";
import { generatedDocApi } from "@/services/api/endpoints";

interface VisualTimelineProps {
    process: Process;
    documents: Document[];
    onStageClick?: (stage: ModalityStage) => void;
    onGenerateAI?: (stage: ModalityStage) => void;
    onCreateEdit?: (stage: ModalityStage, existingDoc?: Document) => void;
    onDismiss?: (stage: ModalityStage) => void;
    activeDocumentTypeId?: string;
}

export const VisualTimeline: React.FC<VisualTimelineProps> = ({
    process,
    documents,
    onStageClick,
    onGenerateAI,
    onCreateEdit,
    onDismiss,
    activeDocumentTypeId,
}) => {
    const [expandedStage, setExpandedStage] = React.useState<string | number | null>(null);

    if (!process || !process.modality || !process.modality.stages) {
        return (
            <div className="p-6 text-center text-[#94a3b8]">
                Selecione um processo para visualizar a linha do tempo.
            </div>
        );
    }

    const stages = [...process.modality.stages].sort((a, b) => a.order - b.order);
    const totalStages = stages.length;

    // Mapeia os documentos já gerados para facilitar a busca
    const documentMap = new Map<string, Document>();
    documents.forEach((doc) => {
        const typeId = typeof doc.document_type === 'object' ? String(doc.document_type.id) : String(doc.document_type);
        // Se a entidade nova tem o doc completo OU ainda não estava em cache, armazena
        if (!documentMap.has(typeId) || doc.has_completed_generated_doc) {
            documentMap.set(typeId, doc);
        }
    });

    // Calcular progresso
    const completedCount = stages.filter(s => {
        const doc = documentMap.get(String(s.id));
        return doc && doc.has_completed_generated_doc;
    }).length;
    const progressPercent = totalStages > 0 ? Math.round((completedCount / totalStages) * 100) : 0;

    // Encontrar a etapa atual (primeira pendente)
    const firstPendingIndex = stages.findIndex(s => {
        const doc = documentMap.get(String(s.id));
        return !doc || !doc.has_completed_generated_doc;
    });
    const currentStage = firstPendingIndex >= 0 ? stages[firstPendingIndex] : null;

    // Auto-expandir a etapa atual
    React.useEffect(() => {
        if (currentStage && expandedStage === null) {
            setExpandedStage(currentStage.id);
        }
    }, [currentStage]);

    const getStageStatus = (stage: ModalityStage, index: number) => {
        const doc = documentMap.get(String(stage.id));
        const isCompleted = doc ? !!doc.has_completed_generated_doc : false;
        const isCurrent = firstPendingIndex === index;
        const isPending = !isCompleted && !isCurrent;
        return { isCompleted, isCurrent, isPending };
    };

    return (
        <div className="w-full space-y-6">
            {/* ===== Progresso Geral do Processo ===== */}
            <Card className="bg-[#0d1117] border-[#1e2835]">
                <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-[#10b981] animate-pulse" />
                            <span className="text-sm font-medium text-[#e2e8f0]">Progresso Geral do Processo</span>
                        </div>
                        <span className={`text-2xl font-bold ${progressPercent > 0 ? 'text-[#10b981]' : 'text-[#f43f5e]'}`}>
                            {progressPercent}%
                        </span>
                    </div>

                    {/* Barra de progresso */}
                    <div className="w-full h-2 bg-[#1e2835] rounded-full overflow-hidden mb-3">
                        <div
                            className="h-full rounded-full transition-all duration-700 ease-out"
                            style={{
                                width: `${progressPercent}%`,
                                background: 'linear-gradient(90deg, #10b981, #3b82f6)',
                            }}
                        />
                    </div>

                    <div className="flex items-center justify-between text-xs text-[#94a3b8]">
                        <span>
                            <Clock className="inline w-3 h-3 mr-1" />
                            {completedCount} de {totalStages} etapas concluídas
                        </span>
                        {currentStage && (
                            <span>
                                <Zap className="inline w-3 h-3 mr-1 text-[#f59e0b]" />
                                Atual: {currentStage.name}
                            </span>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* ===== Título da Timeline ===== */}
            <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#f59e0b]" />
                <h2 className="text-lg font-bold text-[#f5f5f0]">Timeline Visual do Processo</h2>
            </div>

            {/* ===== Lista Vertical de Estágios ===== */}
            <div className="space-y-3">
                {stages.map((stage, index) => {
                    const { isCompleted, isCurrent, isPending } = getStageStatus(stage, index);
                    const generatedDoc = documentMap.get(String(stage.id));
                    const isExpanded = expandedStage === stage.id || isCurrent;

                    // Cores e estilos por estado (Pendentes aparecem cinzentas)
                    let cardBg = "bg-slate-900/50 grayscale opacity-50"; 
                    let cardBorder = "border-slate-800";
                    let iconBg = "bg-slate-800";
                    let iconColor = "text-slate-500";
                    let statusBadgeBg = "bg-slate-800 text-slate-400";
                    let statusText = "Aguardando";

                    if (isCompleted) {
                        // Concluídas ficam em highlight com verde vibrante
                        cardBg = "bg-[#064e3b]/30 shadow-md shadow-[#10b981]/10";
                        cardBorder = "border-[#10b981]/60";
                        iconBg = "bg-[#10b981]/20";
                        iconColor = "text-[#10b981]";
                        statusBadgeBg = "bg-[#10b981]/20 text-[#10b981] border-[#10b981]/40";
                        statusText = "Concluído";
                    } else if (isCurrent) {
                        cardBg = "bg-gradient-to-r from-[#1e2835] to-[#0d1117]";
                        cardBorder = "border-[#3b82f6]/50";
                        iconBg = "bg-[#3b82f6]/20";
                        iconColor = "text-[#3b82f6]";
                        statusBadgeBg = "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30";
                        statusText = "Em Andamento";
                    }

                    return (
                        <Card
                            key={stage.id}
                            className={`${cardBg} ${cardBorder} border transition-all duration-300 cursor-pointer hover:border-[#3b82f6]/40`}
                            onClick={() => {
                                setExpandedStage(expandedStage === stage.id ? null : stage.id);
                            }}
                        >
                            <CardContent className={`${isExpanded ? 'p-5' : 'p-4'}`}>
                                <div className="flex items-start gap-4">
                                    {/* Ícone de documento */}
                                    <div className={`${iconBg} w-14 h-14 rounded-xl flex items-center justify-center shrink-0`}>
                                        {isCompleted ? (
                                            <Check className={`w-7 h-7 ${iconColor}`} />
                                        ) : (
                                            <FileText className={`w-7 h-7 ${iconColor}`} />
                                        )}
                                    </div>

                                    {/* Conteúdo principal */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <h3 className={`font-bold ${isExpanded ? 'text-lg' : 'text-base'} text-[#f5f5f0] truncate`}>
                                                {stage.name}
                                            </h3>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {isExpanded ? (
                                                    <ChevronUp className="w-4 h-4 text-[#64748b]" />
                                                ) : (
                                                    <ChevronDown className="w-4 h-4 text-[#64748b]" />
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge className={`${statusBadgeBg} border text-xs px-2 py-0.5`}>
                                                {isCurrent && <Clock className="inline w-3 h-3 mr-1" />}
                                                {isCompleted && <Check className="inline w-3 h-3 mr-1" />}
                                                {statusText}
                                            </Badge>
                                            {/* Step counter */}
                                            <Badge className="bg-[#3b82f6]/20 text-[#3b82f6] border-[#3b82f6]/30 border text-xs px-2 py-0.5">
                                                {stage.order}/{totalStages}
                                            </Badge>
                                            {stage.is_required && (
                                                <Badge className="bg-[#f43f5e]/20 text-[#f43f5e] border-[#f43f5e]/30 border text-xs px-2 py-0.5">
                                                    Obrigatório
                                                </Badge>
                                            )}
                                        </div>

                                        {/* Conteúdo expandido */}
                                        {isExpanded && (
                                            <div className="mt-3 space-y-3">
                                                {/* Barra de progresso da etapa */}
                                                <div>
                                                    <div className="flex items-center justify-between text-xs text-[#94a3b8] mb-1">
                                                        <span>Progresso da Etapa</span>
                                                        <span className={isCompleted ? 'text-[#10b981]' : 'text-[#94a3b8]'}>
                                                            {isCompleted ? '100%' : isCurrent ? '25%' : '0%'}
                                                        </span>
                                                    </div>
                                                    <div className="w-full h-1.5 bg-[#1e2835] rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full transition-all duration-500"
                                                            style={{
                                                                width: isCompleted ? '100%' : isCurrent ? '25%' : '0%',
                                                                background: isCompleted
                                                                    ? '#10b981'
                                                                    : isCurrent
                                                                        ? 'linear-gradient(90deg, #f59e0b, #f43f5e)'
                                                                        : '#1e2835',
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Doc existente */}
                                                {generatedDoc && (
                                                    generatedDoc.completed_generated_doc_id ? (
                                                        <GeneratedDocumentLink
                                                            generatedDocId={generatedDoc.completed_generated_doc_id}
                                                            documentName={generatedDoc.name}
                                                            className="w-full text-[#10b981] bg-[#10b981]/10 px-3 py-2.5 rounded-lg border border-[#10b981]/20 hover:bg-[#10b981]/15 transition-colors"
                                                        />
                                                    ) : (
                                                        <div className="flex items-center gap-2 text-xs text-[#10b981] bg-[#10b981]/10 p-2 rounded-lg">
                                                            <FileText className="w-3.5 h-3.5 shrink-0" />
                                                            <span className="truncate">{generatedDoc.name}</span>
                                                        </div>
                                                    )
                                                )}

                                                {/* Botões de ação */}
                                                <div className="flex flex-wrap gap-2 pt-1">
                                                    <Button
                                                        size="sm"
                                                        className="bg-[#3b82f6] hover:bg-[#2563eb] text-white text-xs px-4 py-2 h-auto rounded-lg shadow-lg shadow-[#3b82f6]/20"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onGenerateAI?.(stage);
                                                        }}
                                                    >
                                                        <Zap className="w-3.5 h-3.5 mr-1.5" />
                                                        Gerar por IA
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        className="bg-[#f59e0b] hover:bg-[#d97706] text-white text-xs px-4 py-2 h-auto rounded-lg shadow-lg shadow-[#f59e0b]/20"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onCreateEdit?.(stage, generatedDoc);
                                                        }}
                                                    >
                                                        <Pencil className="w-3.5 h-3.5 mr-1.5" />
                                                        Criar/Editar
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="border-[#f43f5e]/50 text-[#f43f5e] hover:bg-[#f43f5e]/10 text-xs px-4 py-2 h-auto rounded-lg"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDismiss?.(stage);
                                                        }}
                                                    >
                                                        <Ban className="w-3.5 h-3.5 mr-1.5" />
                                                        Dispensar
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};
